/**
 * Workspaces Management Page
 * إدارة مساحات وباقات العمل - للخدمات من نوع "مساحات عمل"
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Briefcase, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, X, Check,
  AlertTriangle, Info, Search, MapPin, Clock, Users, DollarSign,
  Building, RefreshCw, ChevronDown, Wifi, Coffee, Printer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/catalog';

const COLORS = {
  primary: '#3B82F6',
  primaryLight: '#DBEAFE',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

const WORKSPACE_TYPES = [
  { value: 'hot_desk', label: 'مكتب مشترك' },
  { value: 'dedicated_desk', label: 'مكتب مخصص' },
  { value: 'private_office', label: 'مكتب خاص' },
  { value: 'meeting_room', label: 'قاعة اجتماعات' },
  { value: 'event_space', label: 'قاعة فعاليات' },
  { value: 'virtual_office', label: 'مكتب افتراضي' },
];

const PACKAGE_DURATIONS = [
  { value: 'hourly', label: 'بالساعة' },
  { value: 'daily', label: 'يومي' },
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'yearly', label: 'سنوي' },
];

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: AlertTriangle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: Check },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '14px 18px', borderRadius: '10px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={18} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff' },
    secondary: { bg: COLORS.secondary, color: '#fff' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border },
    ghost: { bg: 'transparent', color: COLORS.textMuted },
    danger: { bg: COLORS.error, color: '#fff' },
    success: { bg: COLORS.success, color: '#fff' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
      fontFamily: "'Tajawal', sans-serif", borderRadius: '10px',
      border: v.border ? `1.5px solid ${v.border}` : 'none',
      backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
    }}>
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      outline: 'none', ...style
    }} />
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: '100%', padding: '12px 14px', borderRadius: '10px',
      border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
      fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
      cursor: 'pointer', outline: 'none', ...style
    }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block', marginBottom: '8px', fontSize: '14px',
        fontWeight: 600, color: COLORS.text,
      }}>
        {label}
        {required && <span style={{ color: COLORS.error, marginRight: '4px' }}>*</span>}
        {hint && <span style={{ fontWeight: 400, color: COLORS.textMuted, marginRight: '8px', fontSize: '12px' }}>({hint})</span>}
      </label>
      {children}
      {error && <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error }}>{error}</p>}
    </div>
  );
}

function WorkspaceCard({ workspace, onEdit, onToggle, onDelete }) {
  const typeInfo = WORKSPACE_TYPES.find(t => t.value === workspace.workspace_type);
  const durationInfo = PACKAGE_DURATIONS.find(d => d.value === workspace.package_duration);
  
  return (
    <div style={{
      padding: '20px', borderRadius: '16px', backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.border}`, opacity: workspace.is_active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          backgroundColor: `${COLORS.primary}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Briefcase size={28} color={COLORS.primary} />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: COLORS.text }}>
              {workspace.name_ar}
            </h3>
            {typeInfo && (
              <span style={{
                padding: '2px 8px', backgroundColor: `${COLORS.primary}10`, borderRadius: '4px',
                fontSize: '11px', color: COLORS.primary, fontWeight: 500,
              }}>{typeInfo.label}</span>
            )}
            {!workspace.is_active && (
              <span style={{
                padding: '2px 8px', backgroundColor: '#FEE2E2', borderRadius: '4px',
                fontSize: '11px', color: COLORS.error, fontWeight: 500,
              }}>معطل</span>
            )}
          </div>
          
          {workspace.short_description && (
            <p style={{ margin: '0 0 10px', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.5 }}>
              {workspace.short_description}
            </p>
          )}
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '10px' }}>
            {workspace.city && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPin size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{workspace.city}</span>
              </div>
            )}
            {workspace.capacity && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Users size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{workspace.capacity} شخص</span>
              </div>
            )}
            {durationInfo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={14} color={COLORS.textMuted} />
                <span style={{ fontSize: '13px', color: COLORS.text }}>{durationInfo.label}</span>
              </div>
            )}
          </div>
          
          {/* Amenities */}
          {workspace.amenities?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {workspace.amenities.map((amenity, i) => (
                <span key={i} style={{
                  padding: '4px 10px', backgroundColor: COLORS.bg, borderRadius: '6px',
                  fontSize: '12px', color: COLORS.text,
                }}>{amenity}</span>
              ))}
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {workspace.is_free ? (
              <span style={{
                padding: '4px 10px', backgroundColor: '#D1FAE5', borderRadius: '6px',
                fontSize: '12px', color: COLORS.success, fontWeight: 500,
              }}>مجاني</span>
            ) : workspace.price && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                <DollarSign size={14} /> {workspace.price} ر.س {durationInfo ? `/ ${durationInfo.label}` : ''}
              </span>
            )}
          </div>
          
          {workspace.service_name_ar && (
            <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: COLORS.bg, borderRadius: '8px' }}>
              <span style={{ fontSize: '12px', color: COLORS.textMuted }}>الخدمة: </span>
              <span style={{ fontSize: '13px', fontWeight: 500, color: COLORS.text }}>{workspace.service_name_ar}</span>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button onClick={() => onToggle(workspace)} title={workspace.is_active ? 'تعطيل' : 'تفعيل'}
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent',
              color: workspace.is_active ? COLORS.success : COLORS.textMuted, cursor: 'pointer' }}>
            {workspace.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          </button>
          <button onClick={() => onEdit(workspace)} title="تعديل"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: COLORS.secondaryLight,
              color: COLORS.secondary, cursor: 'pointer' }}>
            <Edit2 size={16} />
          </button>
          <button onClick={() => onDelete(workspace)} title="حذف"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer' }}>
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceModal({ isOpen, onClose, onSave, services, cities, editingItem }) {
  const [formData, setFormData] = useState({
    service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
    workspace_type: 'hot_desk', package_duration: 'monthly', capacity: '',
    city: '', branch_name: '', amenities: [], price: '', is_free: false,
    booking_link: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const workspaceServices = services.filter(s => 
    s.category_type === 'workspace' || s.category_name_ar?.includes('مساح')
  );
  
  const AMENITIES_OPTIONS = ['واي فاي', 'طباعة', 'قهوة ومشروبات', 'قاعة اجتماعات', 'استقبال', 'مواقف', 'تكييف'];
  
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setFormData({
          service_id: editingItem.service_id || '',
          name_ar: editingItem.name_ar || '',
          name_en: editingItem.name_en || '',
          short_description: editingItem.short_description || '',
          details: editingItem.details || '',
          workspace_type: editingItem.workspace_type || 'hot_desk',
          package_duration: editingItem.package_duration || 'monthly',
          capacity: editingItem.capacity || '',
          city: editingItem.city || '',
          branch_name: editingItem.branch_name || '',
          amenities: editingItem.amenities || [],
          price: editingItem.price || '',
          is_free: editingItem.is_free === true,
          booking_link: editingItem.booking_link || '',
        });
      } else {
        setFormData({
          service_id: '', name_ar: '', name_en: '', short_description: '', details: '',
          workspace_type: 'hot_desk', package_duration: 'monthly', capacity: '',
          city: '', branch_name: '', amenities: [], price: '', is_free: false,
          booking_link: '',
        });
      }
      setError('');
    }
  }, [isOpen, editingItem]);
  
  const toggleAmenity = (amenity) => {
    if (formData.amenities.includes(amenity)) {
      setFormData({ ...formData, amenities: formData.amenities.filter(a => a !== amenity) });
    } else {
      setFormData({ ...formData, amenities: [...formData.amenities, amenity] });
    }
  };
  
  const validate = () => {
    if (!formData.service_id) return 'يجب اختيار الخدمة';
    if (!formData.name_ar.trim()) return 'اسم المساحة بالعربية مطلوب';
    return null;
  };
  
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave({
        ...formData,
        resource_type: 'workspace_package',
        capacity: parseInt(formData.capacity) || null,
        price: formData.is_free ? null : parseFloat(formData.price) || null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} />
      
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '750px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        backgroundColor: COLORS.white, borderRadius: '16px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, backgroundColor: COLORS.white, zIndex: 1,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
              {editingItem ? 'تعديل مساحة العمل' : 'إضافة مساحة عمل جديدة'}
            </h2>
          </div>
          <button onClick={onClose} style={{
            width: '36px', height: '36px', borderRadius: '8px', border: 'none',
            backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        <div style={{ padding: '24px' }}>
          {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
          
          {workspaceServices.length === 0 && (
            <Alert type="warning" style={{ marginBottom: '20px' }}>
              لا توجد خدمات مساحات عمل في الكتالوج. يرجى إضافة خدمة من نوع "مساحات عمل" أولاً.
            </Alert>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="الخدمة" required>
              <Select value={formData.service_id}
                onChange={e => setFormData({ ...formData, service_id: e.target.value })}
                placeholder="-- اختر الخدمة --"
                options={workspaceServices.map(s => ({ value: s.service_id, label: s.name_ar }))} />
            </FormField>
            <FormField label="نوع المساحة">
              <Select value={formData.workspace_type}
                onChange={e => setFormData({ ...formData, workspace_type: e.target.value })}
                options={WORKSPACE_TYPES} />
            </FormField>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="اسم الباقة بالعربية" required>
              <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
                placeholder="مثال: باقة رواد الأعمال" />
            </FormField>
            <FormField label="اسم الباقة بالإنجليزية">
              <Input value={formData.name_en} onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="Example: Entrepreneurs Package" />
            </FormField>
          </div>
          
          <FormField label="وصف مختصر">
            <Input value={formData.short_description} onChange={e => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="مساحة عمل مشتركة مجهزة بالكامل" />
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <FormField label="مدة الاشتراك">
              <Select value={formData.package_duration}
                onChange={e => setFormData({ ...formData, package_duration: e.target.value })}
                options={PACKAGE_DURATIONS} />
            </FormField>
            <FormField label="السعة">
              <Input type="number" value={formData.capacity} onChange={e => setFormData({ ...formData, capacity: e.target.value })}
                placeholder="عدد الأشخاص" />
            </FormField>
            <FormField label="المدينة">
              <Select value={formData.city}
                onChange={e => setFormData({ ...formData, city: e.target.value })}
                placeholder="-- اختر المدينة --"
                options={cities.map(c => ({ value: c.name_ar, label: c.name_ar }))} />
            </FormField>
          </div>
          
          <FormField label="اسم الفرع/الموقع">
            <Input value={formData.branch_name} onChange={e => setFormData({ ...formData, branch_name: e.target.value })}
              placeholder="مثال: مبنى الابتكار - حي العليا" />
          </FormField>
          
          <FormField label="المرافق والخدمات">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {AMENITIES_OPTIONS.map(amenity => (
                <button key={amenity} type="button" onClick={() => toggleAmenity(amenity)}
                  style={{
                    padding: '8px 14px', borderRadius: '8px',
                    border: `1.5px solid ${formData.amenities.includes(amenity) ? COLORS.primary : COLORS.border}`,
                    backgroundColor: formData.amenities.includes(amenity) ? `${COLORS.primary}10` : COLORS.white,
                    color: formData.amenities.includes(amenity) ? COLORS.primary : COLORS.text,
                    cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                  }}>
                  {formData.amenities.includes(amenity) && <Check size={14} style={{ marginLeft: '4px' }} />}
                  {amenity}
                </button>
              ))}
            </div>
          </FormField>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <FormField label="السعر">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_free}
                    onChange={e => setFormData({ ...formData, is_free: e.target.checked, price: e.target.checked ? '' : formData.price })} />
                  مجاني
                </label>
                {!formData.is_free && (
                  <Input type="number" value={formData.price}
                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                    placeholder="السعر بالريال" style={{ width: '150px' }} />
                )}
              </div>
            </FormField>
            <FormField label="رابط الحجز">
              <Input value={formData.booking_link} onChange={e => setFormData({ ...formData, booking_link: e.target.value })}
                placeholder="https://..." />
            </FormField>
          </div>
        </div>
        
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: COLORS.bg,
          position: 'sticky', bottom: 0,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {editingItem ? 'حفظ التعديلات' : 'إضافة المساحة'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState('');
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, svcRes, citiesRes] = await Promise.all([
        axios.get(`${API}/resources`, { params: { resource_type: 'workspace_package' }, headers: getAuthHeaders() }),
        axios.get(`${API}/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cities`, { headers: getAuthHeaders() }),
      ]);
      
      setWorkspaces(resRes.data || []);
      const svcsData = svcRes.data.items || svcRes.data;
      setServices(Array.isArray(svcsData) ? svcsData : []);
      setCities(citiesRes.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => { fetchData(); }, [fetchData]);
  
  const handleSave = async (data) => {
    if (editingItem) {
      await axios.put(`${API}/resources/${editingItem.resource_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/resources`, data, { headers: getAuthHeaders() });
    }
    fetchData();
    setEditingItem(null);
  };
  
  const handleToggle = async (item) => {
    await axios.post(`${API}/resources/${item.resource_id}/toggle`, {}, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const handleDelete = async (item) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${item.name_ar}"؟`)) return;
    await axios.delete(`${API}/resources/${item.resource_id}`, { headers: getAuthHeaders() });
    fetchData();
  };
  
  const filtered = workspaces.filter(w => {
    if (filterService && w.service_id !== filterService) return false;
    if (searchQuery && !w.name_ar?.includes(searchQuery)) return false;
    return true;
  });
  
  const activeCount = workspaces.filter(w => w.is_active).length;
  
  return (
    <div data-testid="workspaces-page" style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: `${COLORS.primary}15`, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Briefcase size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: COLORS.text }}>مساحات وباقات العمل</h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة مساحات العمل المشتركة والباقات
            </p>
          </div>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'إجمالي المساحات', value: workspaces.length, color: COLORS.primary },
          { label: 'مساحات نشطة', value: activeCount, color: COLORS.success },
          { label: 'مساحات معطلة', value: workspaces.length - activeCount, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '14px', color: COLORS.textMuted }}>{stat.label}</div>
          </div>
        ))}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث بالاسم..."
            style={{ paddingRight: '40px' }} />
        </div>
        <Select value={filterService} onChange={e => setFilterService(e.target.value)}
          placeholder="كل الخدمات"
          options={services.filter(s => s.category_type === 'workspace' || s.category_name_ar?.includes('مساح'))
            .map(s => ({ value: s.service_id, label: s.name_ar }))}
          style={{ width: '220px' }} />
        <Button data-testid="add-workspace-btn" variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
          إضافة مساحة
        </Button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
            <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px', backgroundColor: COLORS.white,
            borderRadius: '16px', border: `1px solid ${COLORS.border}`,
          }}>
            <Briefcase size={48} color={COLORS.textMuted} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
              لا توجد مساحات عمل
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: COLORS.textMuted }}>
              {searchQuery || filterService ? 'لا توجد نتائج تطابق البحث' : 'قم بإضافة مساحات عمل جديدة'}
            </p>
            <Button variant="primary" icon={Plus} onClick={() => { setEditingItem(null); setShowModal(true); }}>
              إضافة أول مساحة
            </Button>
          </div>
        ) : (
          filtered.map(w => (
            <WorkspaceCard key={w.resource_id} workspace={w}
              onEdit={(item) => { setEditingItem(item); setShowModal(true); }}
              onToggle={handleToggle} onDelete={handleDelete} />
          ))
        )}
      </div>
      
      <WorkspaceModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); }}
        onSave={handleSave}
        services={services}
        cities={cities}
        editingItem={editingItem}
      />
    </div>
  );
}
